import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import DriverBottomNav from "@/components/DriverBottomNav";
import { 
  ArrowLeft, 
  User, 
  Car, 
  Upload, 
  FileText, 
  CreditCard, 
  CheckCircle, 
  AlertCircle,
  ExternalLink,
  LogOut
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import PushToggle from "@/components/PushToggle";

import { API } from "@/lib/api";


export default function DriverProfile() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout, updateUser, isDriver } = useAuth();
  const [documents, setDocuments] = useState({ license_photo: null, insurance_photo: null });
  const [uploading, setUploading] = useState({ license: false, insurance: false });
  const [stripeStatus, setStripeStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);

  // Check for Stripe redirect params
  useEffect(() => {
    const stripeSuccess = searchParams.get("stripe_success");
    const stripeRefresh = searchParams.get("stripe_refresh");
    
    if (stripeSuccess === "true") {
      toast.success("Stripe account setup complete!");
      fetchStripeStatus();
    } else if (stripeRefresh === "true") {
      toast.info("Please complete your Stripe setup");
    }
  }, [searchParams]);

  const fetchStripeStatus = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/driver/stripe/status`);
      setStripeStatus(response.data);
    } catch (err) {
      console.error("Failed to fetch Stripe status:", err);
    }
  }, [user]);

  const fetchDocuments = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/driver/documents`);
      setDocuments(response.data);
    } catch (err) {
      console.error("Failed to fetch documents:", err);
    }
  }, [user]);

  useEffect(() => {
    if (user && isDriver) {
      fetchDocuments();
      fetchStripeStatus();
    }
  }, [user, isDriver, fetchDocuments, fetchStripeStatus]);

  const handleFileUpload = async (type, file) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append("file", file);
    
    setUploading(prev => ({ ...prev, [type === "license_photo" ? "license" : "insurance"]: true }));
    
    try {
      await axios.post(`${API}/driver/documents/${type}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`${type === "license_photo" ? "License" : "Insurance"} uploaded successfully`);
      fetchDocuments();
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(prev => ({ ...prev, [type === "license_photo" ? "license" : "insurance"]: false }));
    }
  };

  const handleStripeConnect = async () => {
    setConnectLoading(true);
    try {
      const response = await axios.post(`${API}/driver/stripe/connect`, {
        origin_url: window.location.origin
      });
      // Redirect to Stripe onboarding
      window.location.href = response.data.url;
    } catch (err) {
      console.error("Stripe connect error:", err);
      toast.error(err.response?.data?.detail || "Failed to connect Stripe");
      setConnectLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/driver");
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-5">
        <Card className="bg-[#121212] border-white/5 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-[#4D88FF] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Login Required</h2>
            <p className="text-zinc-400 mb-4">Please sign in to access your profile</p>
            <Button 
              onClick={() => navigate("/login?redirect=/driver/profile")}
              className="bg-[#0047FF] hover:bg-[#003BE0] text-white rounded-xl border-0"
            >
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getStatusBadge = () => {
    const status = user.driver_status || "pending";
    const colors = {
      pending: "bg-[#0047FF]/10 text-[#4D88FF] border-[#0047FF]/25",
      approved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
      suspended: "bg-red-500/10 text-red-400 border-red-500/20"
    };
    return (
      <Badge className={colors[status]}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  return (
    <div className="h-[100dvh] flex flex-col bg-[#050505]">
      {/* Header */}
      <header className="flex-shrink-0 z-10 bg-[#050505]/95 backdrop-blur border-b border-white/5">
        <div className="px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/driver")}
              className="text-zinc-400 hover:text-white hover:bg-zinc-800"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-white">Driver Profile</h1>
              <p className="text-sm text-zinc-500">{user.email}</p>
            </div>
          </div>
          {getStatusBadge()}
        </div>
      </header>

      <main className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-6 pb-24 max-w-lg mx-auto w-full space-y-6">
        {/* Personal Info */}
        <Card className="bg-[#121212] border-white/5" data-testid="personal-info-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#0047FF]/10">
                <User className="w-5 h-5 text-[#4D88FF]" />
              </div>
              <CardTitle className="text-lg text-white">Personal Info</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-zinc-500">Name</p>
                <p className="text-white">{user.first_name} {user.last_name}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-500">Phone</p>
                <p className="text-white">{user.phone}</p>
              </div>
            </div>
            <div>
              <p className="text-xs text-zinc-500">Address</p>
              <p className="text-white">{user.address}, {user.city}, {user.state} {user.zip_code}</p>
            </div>
          </CardContent>
        </Card>

        {/* Vehicle Info */}
        <Card className="bg-[#121212] border-white/5" data-testid="vehicle-info-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#0047FF]/10">
                <Car className="w-5 h-5 text-[#4D88FF]" />
              </div>
              <CardTitle className="text-lg text-white">Vehicle Info</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-zinc-500">Vehicle</p>
                <p className="text-white">
                  {user.vehicle_year} {user.vehicle_make} {user.vehicle_model}
                </p>
              </div>
              <div>
                <p className="text-xs text-zinc-500">Color</p>
                <p className="text-white">{user.vehicle_color || "N/A"}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-zinc-500">License Plate</p>
                <p className="text-white">{user.license_plate}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-500">Driver's License</p>
                <p className="text-white">{user.drivers_license}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Documents Upload */}
        <Card className="bg-[#121212] border-white/5" data-testid="documents-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#0047FF]/10">
                <FileText className="w-5 h-5 text-[#4D88FF]" />
              </div>
              <div>
                <CardTitle className="text-lg text-white">Documents</CardTitle>
                <CardDescription className="text-zinc-500">Upload your license and insurance</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* License Photo */}
            <div className="p-4 rounded-lg bg-zinc-800/50 border border-white/10">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white">Driver's License Photo</span>
                  {documents.license_photo ? (
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-[#4D88FF]" />
                  )}
                </div>
                {documents.license_photo && (
                  <span className="text-xs text-zinc-500">
                    Uploaded {new Date(documents.license_photo.uploaded_at).toLocaleDateString()}
                  </span>
                )}
              </div>
              <Label className="cursor-pointer">
                <Input
                  type="file"
                  accept="image/*,.pdf"
                  className="hidden"
                  onChange={(e) => handleFileUpload("license_photo", e.target.files[0])}
                  disabled={uploading.license}
                  data-testid="license-upload"
                />
                <div className="flex items-center justify-center gap-2 py-3 px-4 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-zinc-300 transition-colors">
                  <Upload className="w-4 h-4" />
                  {uploading.license ? "Uploading..." : documents.license_photo ? "Replace" : "Upload"}
                </div>
              </Label>
            </div>

            {/* Insurance Photo */}
            <div className="p-4 rounded-lg bg-zinc-800/50 border border-white/10">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white">Insurance Card Photo</span>
                  {documents.insurance_photo ? (
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-[#4D88FF]" />
                  )}
                </div>
                {documents.insurance_photo && (
                  <span className="text-xs text-zinc-500">
                    Uploaded {new Date(documents.insurance_photo.uploaded_at).toLocaleDateString()}
                  </span>
                )}
              </div>
              <Label className="cursor-pointer">
                <Input
                  type="file"
                  accept="image/*,.pdf"
                  className="hidden"
                  onChange={(e) => handleFileUpload("insurance_photo", e.target.files[0])}
                  disabled={uploading.insurance}
                  data-testid="insurance-upload"
                />
                <div className="flex items-center justify-center gap-2 py-3 px-4 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-zinc-300 transition-colors">
                  <Upload className="w-4 h-4" />
                  {uploading.insurance ? "Uploading..." : documents.insurance_photo ? "Replace" : "Upload"}
                </div>
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Banking / Stripe Connect */}
        <Card className="bg-[#121212] border-white/5" data-testid="banking-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#0047FF]/10">
                <CreditCard className="w-5 h-5 text-[#4D88FF]" />
              </div>
              <div>
                <CardTitle className="text-lg text-white">Banking & Payouts</CardTitle>
                <CardDescription className="text-zinc-500">Connect your bank for payouts</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {stripeStatus?.connected ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <div>
                      <p className="text-sm font-medium text-white">Stripe Connected</p>
                      <p className="text-xs text-zinc-400">
                        {stripeStatus.payouts_enabled ? "Payouts enabled" : "Complete setup to receive payouts"}
                      </p>
                    </div>
                  </div>
                  <Badge className={stripeStatus.payouts_enabled 
                    ? "bg-emerald-500/10 text-emerald-400" 
                    : "bg-[#0047FF]/10 text-[#4D88FF]"
                  }>
                    {stripeStatus.status}
                  </Badge>
                </div>
                {!stripeStatus.payouts_enabled && (
                  <Button
                    onClick={handleStripeConnect}
                    disabled={connectLoading}
                    className="w-full bg-[#0047FF] hover:bg-[#003BE0] text-white rounded-xl border-0"
                  >
                    {connectLoading ? "Loading..." : "Complete Setup"}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-zinc-400">
                  Connect your bank account via Stripe to receive payouts for completed deliveries.
                </p>
                <Button
                  onClick={handleStripeConnect}
                  disabled={connectLoading}
                  className="w-full bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl shadow-[0_4px_24px_-8px_rgba(0,71,255,0.6)] border-0"
                  data-testid="connect-stripe-btn"
                >
                  {connectLoading ? "Loading..." : "Connect Bank Account"}
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
                <p className="text-xs text-zinc-500 text-center">
                  Powered by Stripe. Your banking info is never stored on our servers.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Logout */}
        <PushToggle description="Get an instant alert every time a new booking drops in your area." />

        <Button
          variant="outline"
          onClick={handleLogout}
          className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </main>
      <DriverBottomNav />
    </div>
  );
}
